import { useId, useMemo, useState, type FormEvent } from 'react';
import { ApiError, Button, Input } from '@dataroom/shared';
import type { CategoryNode } from '@dataroom/shared';
import { useCreateFolder, type FolderTier } from '@/api/hooks';
import { FOLDER_AUDIENCES } from '@/lib/folder-audience';
import { Modal } from './Modal';

const SYNTHETIC_IDS = new Set(['uncategorized', 'demo-cat-uncat']);

interface FolderOption {
  id: string;
  /** Root-to-folder names. */
  path: string[];
}

function flatten(nodes: CategoryNode[], parent: string[] = []): FolderOption[] {
  const out: FolderOption[] = [];
  for (const node of nodes) {
    if (SYNTHETIC_IDS.has(node.id)) continue;
    const path = [...parent, node.name];
    out.push({ id: node.id, path });
    out.push(...flatten(node.children, path));
  }
  return out;
}

/** The import body for a new folder: inside `parentId` (by id — never by name,
 * which could match another folder or fall back to the top level), sharing its
 * tier; or at the top level with the chosen `tier`. */
export function newFolderBody(name: string, parentId: string | null, tier: FolderTier) {
  return parentId
    ? { nodes: [{ name }], parentTagId: parentId }
    : { nodes: [{ name }], unlockStage: tier };
}

/**
 * "New folder" (found missing by the full-deal test 2026-10-08): the only
 * folders in a room were the ones its checklist created, so an intermediary had
 * nowhere to put the teaser and the CIM. A folder is created through the
 * hierarchy import with its TIER — which decides who gets it by default when
 * invited (ADR-0010 §2): the teaser tier goes to every invited buyer; the CIM
 * and due-diligence tiers only by an explicit grant. The audience is chosen for
 * a TOP-LEVEL folder only; a folder inside another always shares its parent's.
 * Nothing is teaser material by default: the most restrictive audience is
 * pre-selected, and the teaser must be chosen.
 */
export function NewFolderDialog({
  roomId,
  categories,
  onClose,
}: {
  roomId: string;
  categories: CategoryNode[];
  onClose: () => void;
}) {
  const create = useCreateFolder(roomId);
  const options = useMemo(() => flatten(categories), [categories]);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [tier, setTier] = useState<FolderTier>('due_diligence');
  const nameId = useId();
  const parentSelectId = useId();
  const tierName = useId();
  const parent = options.find((o) => o.id === parentId) ?? null;
  const trimmed = name.trim();
  const error = create.error
    ? create.error instanceof ApiError
      ? create.error.message
      : 'Something went wrong. Please try again.'
    : null;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!trimmed || create.isPending) return;
    // Inside a folder: no tier of its own — it shares the parent's.
    create.mutate(newFolderBody(trimmed, parent?.id ?? null, tier), { onSuccess: onClose });
  };

  return (
    <Modal
      title="New folder"
      description="Add a folder for material the checklist does not cover — the teaser, the CIM, management presentations."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => submit()} disabled={!trimmed || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add folder'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submit} noValidate>
        <div className="space-y-1.5">
          <label htmlFor={nameId} className="text-sm font-medium">
            Folder name
          </label>
          <Input
            id={nameId}
            value={name}
            maxLength={200}
            placeholder="e.g. Deal Materials"
            onChange={(e) => setName(e.target.value)}
            disabled={create.isPending}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={parentSelectId} className="text-sm font-medium">
            Inside
          </label>
          <select
            id={parentSelectId}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={parentId}
            disabled={create.isPending}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">Top level</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.path.join(' › ')}
              </option>
            ))}
          </select>
        </div>
        {parent ? (
          <p className="text-sm text-muted-foreground">
            It gets the same default audience as “{parent.path[parent.path.length - 1]}”. For the
            CIM or other material only some buyers should see, add a top-level folder instead.
          </p>
        ) : (
          <fieldset className="space-y-2" disabled={create.isPending}>
            <legend className="text-sm font-medium">Who gets it by default</legend>
            {FOLDER_AUDIENCES.map((t) => (
              <label key={t.value} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={tierName}
                  className="mt-1"
                  checked={tier === t.value}
                  onChange={() => setTier(t.value)}
                />
                <span>
                  {t.label}
                  <span className="block text-xs text-muted-foreground">{t.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
